import { SyntheticEvent, useRef, useState } from 'react';
import { clampFraction } from './clampFraction';

export type PlayableSegment = {
  /** Unique across recordings — a segment id alone is only unique within one. */
  key: string;
  fileId: string;
  startSeconds: number;
  endSeconds: number;
};

type SegmentPlayback = {
  /** Attach to each recording's mounted `<audio>`; playback is driven off them, not `new Audio()`. */
  registerAudio: (fileId: string) => (element: HTMLAudioElement | null) => void;
  isPlaying: (key: string) => boolean;
  /** Playback position within the given segment, 0..1, or null when it isn't holding the playhead. */
  progressFor: (key: string) => number | null;
  toggle: (segment: PlayableSegment) => void;
  seek: (segment: PlayableSegment, fraction: number) => void;
  handleTimeUpdate: () => void;
  handleEnded: () => void;
  handleError: (event: SyntheticEvent<HTMLAudioElement>) => void;
};

// A short tail past the cited end so auto-pause doesn't clip the last word when the
// transcription's end marker lands slightly early.
const AUTO_PAUSE_TAIL_SECONDS = 0.3;

function positionAt(segment: PlayableSegment, fraction: number): number {
  const span = segment.endSeconds - segment.startSeconds;
  return segment.startSeconds + span * clampFraction(fraction);
}

function resumePosition(audio: HTMLAudioElement, segment: PlayableSegment): number {
  const pausedWithinSegment =
    audio.currentTime >= segment.startSeconds && audio.currentTime < segment.endSeconds;
  return pausedWithinSegment ? audio.currentTime : segment.startSeconds;
}

/**
 * Plays a segment's [start, end] range on its own recording's mounted `<audio>` element: seeks to
 * the start, pauses just past the end, and reports progress. A document can be written from
 * several meetings, so each recording mounts its own element and a segment says which it belongs
 * to. Only one segment plays at a time; toggling the active segment pauses it.
 */
export function useSegmentPlayback(): SegmentPlayback {
  const audiosRef = useRef(new Map<string, HTMLAudioElement>());
  const activeSegmentRef = useRef<PlayableSegment | null>(null);
  const soundingRef = useRef(false);
  const playAttemptsRef = useRef(0);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [isSounding, setIsSounding] = useState(false);
  const [progress, setProgress] = useState(0);

  const registerAudio = (fileId: string) => (element: HTMLAudioElement | null) => {
    if (element) audiosRef.current.set(fileId, element);
    else audiosRef.current.delete(fileId);
  };

  const activeAudio = () => {
    const segment = activeSegmentRef.current;
    return segment ? (audiosRef.current.get(segment.fileId) ?? null) : null;
  };

  const setActive = (segment: PlayableSegment | null) => {
    activeSegmentRef.current = segment;
    setActiveKey(segment?.key ?? null);
  };

  // Counting the attempts also retires them: a `play()` still in flight when the intent changes
  // rejects later, and its handler must not reset a segment that has since been paused or replaced.
  const setSounding = (value: boolean) => {
    soundingRef.current = value;
    playAttemptsRef.current += 1;
    setIsSounding(value);
  };

  const stop = () => {
    setActive(null);
    setSounding(false);
    setProgress(0);
  };

  const pauseActive = () => {
    activeAudio()?.pause();
    setSounding(false);
  };

  const start = (audio: HTMLAudioElement, from: number) => {
    audio.currentTime = from;
    setSounding(true);
    const attempt = playAttemptsRef.current;
    void audio.play().catch(() => {
      if (playAttemptsRef.current === attempt) stop();
    });
  };

  const handleTimeUpdate = () => {
    const audio = activeAudio();
    const segment = activeSegmentRef.current;
    if (!audio || !segment) return;
    if (audio.currentTime >= segment.endSeconds + AUTO_PAUSE_TAIL_SECONDS) {
      audio.pause();
      stop();
      return;
    }
    const span = segment.endSeconds - segment.startSeconds;
    const elapsed = audio.currentTime - segment.startSeconds;
    setProgress(span > 0 ? clampFraction(elapsed / span) : 0);
  };

  const handleEnded = () => stop();

  // Every recording's element reports errors here, but only a failure of the one holding the
  // playhead should stop anything — another recording failing to load is not this segment's problem.
  const handleError = (event: SyntheticEvent<HTMLAudioElement>) => {
    const audio = activeAudio();
    if (event.currentTarget !== audio) return;
    audio.pause();
    stop();
  };

  const toggleActive = (audio: HTMLAudioElement, segment: PlayableSegment) => {
    if (soundingRef.current) {
      audio.pause();
      setSounding(false);
      return;
    }
    start(audio, resumePosition(audio, segment));
  };

  const startFresh = (audio: HTMLAudioElement, segment: PlayableSegment) => {
    // Another recording may be mid-play; only one segment sounds at a time.
    pauseActive();
    audio.pause();
    setActive(segment);
    setProgress(0);
    start(audio, segment.startSeconds);
  };

  const toggle = (segment: PlayableSegment) => {
    const audio = audiosRef.current.get(segment.fileId);
    if (!audio) return;

    // Decide on the ref, which is always current — `activeKey` state can lag a just-committed reset.
    const isActive = activeSegmentRef.current?.key === segment.key;
    if (isActive) toggleActive(audio, segment);
    else startFresh(audio, segment);
  };

  const seek = (segment: PlayableSegment, fraction: number) => {
    const audio = audiosRef.current.get(segment.fileId);
    if (!audio) return;

    if (activeSegmentRef.current?.key !== segment.key) {
      pauseActive();
      audio.pause();
      setActive(segment);
    }
    audio.currentTime = positionAt(segment, fraction);
    setProgress(clampFraction(fraction));
  };

  return {
    registerAudio,
    isPlaying: (key: string) => activeKey === key && isSounding,
    progressFor: (key: string) => (activeKey === key ? progress : null),
    toggle,
    seek,
    handleTimeUpdate,
    handleEnded,
    handleError,
  };
}
